import axios from 'axios';
import { create } from 'zustand';
import { campaignsApi } from '../lib/api';
import type { AttachmentSelector, AudiencePreview, Campaign, CampaignRun } from '../types/campaign';

/** The server's message off an Axios rejection, falling back to the error's own message. */
function serverMessage(e: unknown, fallback: string): string {
  if (axios.isAxiosError(e)) {
    const msg = e.response?.data?.message;
    if (typeof msg === 'string' && msg) return msg;
  }
  return e instanceof Error && e.message ? e.message : fallback;
}

interface CampaignState {
  campaigns: Campaign[];
  isLoading: boolean;
  error: string | null;
  preview: AudiencePreview | null;
  previewError: string | null;
  isPreviewLoading: boolean;
  fetchCampaigns: () => Promise<void>;
  previewAudience: (listId: string, selector: AttachmentSelector, imageSetKey?: string | null) => Promise<void>;
  clearPreview: () => void;
  sendNow: (id: string, confirmOverLimit?: boolean) => Promise<CampaignRun | null>;
}

// Identifies the latest preview request. Every request and every invalidation
// advances it, so a response that resolves after the selection changed (or
// after a newer request started) is ignored instead of repopulating the panel.
let previewGeneration = 0;

export const useCampaignStore = create<CampaignState>((set) => ({
  campaigns: [],
  isLoading: false,
  error: null,
  preview: null,
  previewError: null,
  isPreviewLoading: false,
  fetchCampaigns: async () => {
    set({ isLoading: true, error: null });
    try {
      const campaigns = await campaignsApi.list();
      set({ campaigns, isLoading: false });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load campaigns', isLoading: false });
    }
  },
  previewAudience: async (listId, selector, imageSetKey) => {
    const generation = ++previewGeneration;
    set({ isPreviewLoading: true, previewError: null, preview: null });
    try {
      const preview = await campaignsApi.previewAudience(listId, selector, imageSetKey);
      if (generation !== previewGeneration) return;
      set({ preview, isPreviewLoading: false });
    } catch (e) {
      if (generation !== previewGeneration) return;
      set({ previewError: serverMessage(e, 'Failed to preview audience'), isPreviewLoading: false });
    }
  },
  clearPreview: () => {
    previewGeneration += 1;
    set({ preview: null, previewError: null, isPreviewLoading: false });
  },
  sendNow: async (id, confirmOverLimit = false) => {
    try {
      return await campaignsApi.sendNow(id, confirmOverLimit);
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to send' });
      return null;
    }
  },
}));
